/**
 * Side drawer showing recent audit-log entries for a single user.
 *
 * Each event renders as a friendly sentence ("Roles updated — added
 * Marketing") via auditDescriptions.ts so non-technical readers can
 * scan it. Raw metadata is tucked behind a per-row "Show details"
 * toggle for the rare case anyone needs the structured payload.
 */

import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { fetchAuditLog, type AuditLogEntry, type UserListItem } from '../../api/admin';
import { describeAuditEvent, relativeTime, type Tone } from './auditDescriptions';

const INK = '#0f0a07';
const IVORY = '#faf6ee';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const TONE_COLORS: Record<Tone, { bg: string; fg: string; dot: string }> = {
  success: { bg: 'rgba(34, 134, 90, 0.10)', fg: '#22865a', dot: '#22865a' },
  error: { bg: 'rgba(178, 47, 47, 0.10)', fg: '#b22f2f', dot: '#b22f2f' },
  info: { bg: 'rgba(50, 110, 184, 0.10)', fg: '#326eb8', dot: '#326eb8' },
  warning: { bg: 'rgba(187, 140, 63, 0.14)', fg: '#a07530', dot: '#bb8c3f' },
  muted: { bg: 'rgba(15, 10, 7, 0.06)', fg: 'rgba(15, 10, 7, 0.5)', dot: 'rgba(15, 10, 7, 0.4)' },
};

interface UserActivityDrawerProps {
  open: boolean;
  user: UserListItem | null;
  onClose: () => void;
}

export function UserActivityDrawer({ open, user, onClose }: UserActivityDrawerProps) {
  const activityQuery = useQuery({
    queryKey: ['admin', 'audit-log', user?.id],
    queryFn: () => fetchAuditLog({ user_id: user!.id, limit: 50 }),
    enabled: !!user && open,
  });

  const items = activityQuery.data?.items ?? [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 460 },
            bgcolor: IVORY,
            backgroundImage: 'none',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: IVORY,
          borderBottom: '1px solid rgba(15, 10, 7, 0.08)',
          px: 2.5,
          py: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: GOLD,
              }}
            >
              Activity
            </Typography>
            {user && (
              <>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontFamily: FONT_INTER,
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: INK,
                  }}
                >
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    color: 'rgba(15, 10, 7, 0.55)',
                    mt: 0.25,
                  }}
                >
                  {user.email}
                </Typography>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2.5, py: 2.5, flex: 1 }}>
        {activityQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: GOLD }} size={28} />
          </Box>
        ) : activityQuery.error ? (
          <Alert severity="error">{(activityQuery.error as Error).message}</Alert>
        ) : items.length === 0 ? (
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15, 10, 7, 0.5)',
              textAlign: 'center',
              py: 4,
            }}
          >
            No activity recorded for this user yet.
          </Typography>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Vertical timeline rail */}
            <Box
              sx={{
                position: 'absolute',
                left: 9,
                top: 14,
                bottom: 14,
                width: '1px',
                bgcolor: 'rgba(15, 10, 7, 0.08)',
              }}
            />
            <Stack spacing={1.75}>
              {items.map((entry) => (
                <ActivityRow key={entry.id} entry={entry} />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

function ActivityRow({ entry }: { entry: AuditLogEntry }) {
  const [showDetails, setShowDetails] = useState(false);
  const event = describeAuditEvent(entry);
  const tone = TONE_COLORS[event.tone];
  const hasMeta = Object.keys(entry.metadata ?? {}).length > 0;
  const absolute = new Date(entry.occurred_at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <Box sx={{ position: 'relative', pl: 3.5 }}>
      {/* Dot */}
      <Box
        sx={{
          position: 'absolute',
          left: 4,
          top: 6,
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: tone.dot,
          border: `2px solid ${IVORY}`,
          boxShadow: `0 0 0 1px ${tone.dot}`,
        }}
      />

      {/* Title */}
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 14,
          fontWeight: 600,
          color: INK,
          lineHeight: 1.35,
        }}
      >
        {event.title}
      </Typography>

      {/* Description */}
      {event.description && (
        <Typography
          sx={{
            mt: 0.35,
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.7)',
            lineHeight: 1.5,
          }}
        >
          {event.description}
        </Typography>
      )}

      {/* Time + IP */}
      <Box sx={{ mt: 0.6, display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap' }}>
        <Typography
          component="span"
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            fontWeight: 600,
            color: 'rgba(15, 10, 7, 0.62)',
          }}
        >
          {relativeTime(entry.occurred_at)}
        </Typography>
        <Box
          component="span"
          sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(15, 10, 7, 0.22)' }}
        />
        <Typography
          component="span"
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.48)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {absolute}
        </Typography>
      </Box>

      {/* "Show details" toggle — only when there's something to show */}
      {hasMeta && (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            disableRipple
            onClick={() => setShowDetails((v) => !v)}
            endIcon={
              showDetails ? (
                <ExpandLessIcon sx={{ fontSize: 14 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              ml: -0.75,
              px: 0.75,
              py: 0.25,
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.4)',
              '&:hover': { color: GOLD, bgcolor: 'transparent' },
            }}
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </Button>
          <Collapse in={showDetails} unmountOnExit>
            <Box
              sx={{
                mt: 0.75,
                p: 1.25,
                borderRadius: 0.75,
                bgcolor: 'rgba(15, 10, 7, 0.03)',
                border: '1px solid rgba(15, 10, 7, 0.06)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 10, 7, 0.4)',
                  mb: 0.5,
                }}
              >
                Event code · {entry.action}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'monospace',
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.62)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(entry.metadata, null, 2)}
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
